import { css, CSSResultGroup, html, LitElement, PropertyValues } from "lit";
import { property } from "lit/decorators.js";

import { customElement, DesignToken } from "@m3e/web/core";

import { HeadingLevel } from "./HeadingLevel";
import { HeadingSize } from "./HeadingSize";
import { HeadingVariant } from "./HeadingVariant";

/**
 * A heading to a page or section.
 *
 * @description
 * The `m3e-heading` component provides expressive, accessible headings for pages and sections, supporting display,
 * headline, title, and label variants in multiple sizes. It applies Material 3 typographic tokens for font size,
 * weight, line height, and letter spacing, ensuring visual hierarchy and clarity.
 *
 * @example
 * The following example illustrates use of the `m3e-heading` to present each variant and size.
 * ```html
 * <m3e-heading variant="display" size="large">Display Large</m3e-heading>
 * <m3e-heading variant="display" size="medium">Display Medium</m3e-heading>
 * <m3e-heading variant="display" size="small">Display Small</m3e-heading>
 * <m3e-heading variant="headline" size="large">Headline Large</m3e-heading>
 * <m3e-heading variant="headline" size="medium">Headline Medium</m3e-heading>
 * <m3e-heading variant="headline" size="small">Headline Small</m3e-heading>
 * <m3e-heading variant="title" size="large">Title Large</m3e-heading>
 * <m3e-heading variant="title" size="medium">Title Medium</m3e-heading>
 * <m3e-heading variant="title" size="small">Title Small</m3e-heading>
 * <m3e-heading variant="label" size="large">Label Large</m3e-heading>
 * <m3e-heading variant="label" size="medium">Label Medium</m3e-heading>
 * <m3e-heading variant="label" size="small">Label Small</m3e-heading>
 * ```
 *
 * @example
 * The next example illustrates use of the `level` attribute to designate the accessibility level of a heading.
 * When specified, ARIA `role="heading"` is applied and the `level` is propagated to `aria-level`.
 * ```html
 * <m3e-heading variant="headline" size="large" level="1">Page title</m3e-heading>
 * ```
 *
 * @tag m3e-heading
 *
 * @slot - Renders the content of the heading.
 *
 * @attr emphasized - Whether the heading uses an emphasized typescale.
 * @attr level - The accessibility level of the heading.
 * @attr size - The size of the heading.
 * @attr variant - The appearance variant of the heading.
 *
 * @cssprop --md-sys-typescale-display-large-font-size - Font size for Display Large text, ideal for hero headlines
 * @cssprop --md-sys-typescale-display-large-font-weight - Font weight for Display Large text
 * @cssprop --md-sys-typescale-display-large-tracking - Letter spacing for Display Large text
 * @cssprop --md-sys-typescale-display-large-line-height - Line height for Display Large text
 * @cssprop --md-sys-typescale-display-medium-font-size - Font size for Display Medium text
 * @cssprop --md-sys-typescale-display-medium-font-weight - Font weight for Display Medium text
 * @cssprop --md-sys-typescale-display-medium-tracking - Letter spacing for Display Medium text
 * @cssprop --md-sys-typescale-display-medium-line-height - Line height for Display Medium text
 * @cssprop --md-sys-typescale-display-small-font-size - Font size for Display Small text
 * @cssprop --md-sys-typescale-display-small-font-weight - Font weight for Display Small text
 * @cssprop --md-sys-typescale-display-small-tracking - Letter spacing for Display Small text
 * @cssprop --md-sys-typescale-display-small-line-height - Line height for Display Small text
 * @cssprop --md-sys-typescale-headline-large-font-size - Font size for Headline Large text
 * @cssprop --md-sys-typescale-headline-large-font-weight - Font weight for Headline Large text
 * @cssprop --md-sys-typescale-headline-large-tracking - Letter spacing for Headline Large text
 * @cssprop --md-sys-typescale-headline-large-line-height - Line height for Headline Large text
 * @cssprop --md-sys-typescale-headline-medium-font-size - Font size for Headline Medium text
 * @cssprop --md-sys-typescale-headline-medium-font-weight - Font weight for Headline Medium text
 * @cssprop --md-sys-typescale-headline-medium-tracking - Letter spacing for Headline Medium text
 * @cssprop --md-sys-typescale-headline-medium-line-height - Line height for Headline Medium text
 * @cssprop --md-sys-typescale-headline-small-font-size - Font size for Headline Small text
 * @cssprop --md-sys-typescale-headline-small-font-weight - Font weight for Headline Small text
 * @cssprop --md-sys-typescale-headline-small-tracking - Letter spacing for Headline Small text
 * @cssprop --md-sys-typescale-headline-small-line-height - Line height for Headline Small text
 * @cssprop --md-sys-typescale-title-large-font-size - Font size for Title Large text
 * @cssprop --md-sys-typescale-title-large-font-weight - Font weight for Title Large text
 * @cssprop --md-sys-typescale-title-large-tracking - Letter spacing for Title Large text
 * @cssprop --md-sys-typescale-title-large-line-height - Line height for Title Large text
 * @cssprop --md-sys-typescale-title-medium-font-size - Font size for Title Medium text
 * @cssprop --md-sys-typescale-title-medium-font-weight - Font weight for Title Medium text
 * @cssprop --md-sys-typescale-title-medium-tracking - Letter spacing for Title Medium text
 * @cssprop --md-sys-typescale-title-medium-line-height - Line height for Title Medium text
 * @cssprop --md-sys-typescale-title-small-font-size - Font size for Title Small text
 * @cssprop --md-sys-typescale-title-small-font-weight - Font weight for Title Small text
 * @cssprop --md-sys-typescale-title-small-tracking - Letter spacing for Title Small text
 * @cssprop --md-sys-typescale-title-small-line-height - Line height for Title Small text
 * @cssprop --md-sys-typescale-label-large-font-size - Font size for Label Large text
 * @cssprop --md-sys-typescale-label-large-font-weight - Font weight for Label Large text
 * @cssprop --md-sys-typescale-label-large-tracking - Letter spacing for Label Large text
 * @cssprop --md-sys-typescale-label-large-line-height - Line height for Label Large text
 * @cssprop --md-sys-typescale-label-medium-font-size - Font size for Label Medium text
 * @cssprop --md-sys-typescale-label-medium-font-weight - Font weight for Label Medium text
 * @cssprop --md-sys-typescale-label-medium-tracking - Letter spacing for Label Medium text
 * @cssprop --md-sys-typescale-label-medium-line-height - Line height for Label Medium text
 * @cssprop --md-sys-typescale-label-small-font-size - Font size for Label Small text
 * @cssprop --md-sys-typescale-label-small-font-weight - Font weight for Label Small text
 * @cssprop --md-sys-typescale-label-small-tracking - Letter spacing for Label Small text
 * @cssprop --md-sys-typescale-label-small-line-height - Line height for Label Small text
 * @cssprop --md-sys-typescale-emphasized-display-large-font-size - Font size for emphasized Display Large text
 * @cssprop --md-sys-typescale-emphasized-display-large-font-weight - Font weight for emphasized Display Large text
 * @cssprop --md-sys-typescale-emphasized-display-large-tracking - Letter spacing for emphasized Display Large text
 * @cssprop --md-sys-typescale-emphasized-display-large-line-height - Line height for emphasized Display Large text
 * @cssprop --md-sys-typescale-emphasized-display-medium-font-size - Font size for emphasized Display Medium text
 * @cssprop --md-sys-typescale-emphasized-display-medium-font-weight - Font weight for emphasized Display Medium text
 * @cssprop --md-sys-typescale-emphasized-display-medium-tracking - Letter spacing for emphasized Display Medium text
 * @cssprop --md-sys-typescale-emphasized-display-medium-line-height - Line height for emphasized Display Medium text
 * @cssprop --md-sys-typescale-emphasized-display-small-font-size - Font size for emphasized Display Small text
 * @cssprop --md-sys-typescale-emphasized-display-small-font-weight - Font weight for emphasized Display Small text
 * @cssprop --md-sys-typescale-emphasized-display-small-tracking - Letter spacing for emphasized Display Small text
 * @cssprop --md-sys-typescale-emphasized-display-small-line-height - Line height for emphasized Display Small text
 * @cssprop --md-sys-typescale-emphasized-headline-large-font-size - Font size for emphasized Headline Large text
 * @cssprop --md-sys-typescale-emphasized-headline-large-font-weight - Font weight for emphasized Headline Large text
 * @cssprop --md-sys-typescale-emphasized-headline-large-tracking - Letter spacing for emphasized Headline Large text
 * @cssprop --md-sys-typescale-emphasized-headline-large-line-height - Line height for emphasized Headline Large text
 * @cssprop --md-sys-typescale-emphasized-headline-medium-font-size - Font size for emphasized Headline Medium text
 * @cssprop --md-sys-typescale-emphasized-headline-medium-font-weight - Font weight for emphasized Headline Medium text
 * @cssprop --md-sys-typescale-emphasized-headline-medium-tracking - Letter spacing for emphasized Headline Medium text
 * @cssprop --md-sys-typescale-emphasized-headline-medium-line-height - Line height for emphasized Headline Medium text
 * @cssprop --md-sys-typescale-emphasized-headline-small-font-size - Font size for emphasized Headline Small text
 * @cssprop --md-sys-typescale-emphasized-headline-small-font-weight - Font weight for emphasized Headline Small text
 * @cssprop --md-sys-typescale-emphasized-headline-small-tracking - Letter spacing for emphasized Headline Small text
 * @cssprop --md-sys-typescale-emphasized-headline-small-line-height - Line height for emphasized Headline Small text
 * @cssprop --md-sys-typescale-emphasized-title-large-font-size - Font size for emphasized Title Large text
 * @cssprop --md-sys-typescale-emphasized-title-large-font-weight - Font weight for emphasized Title Large text
 * @cssprop --md-sys-typescale-emphasized-title-large-tracking - Letter spacing for emphasized Title Large text
 * @cssprop --md-sys-typescale-emphasized-title-large-line-height - Line height for emphasized Title Large text
 * @cssprop --md-sys-typescale-emphasized-title-medium-font-size - Font size for emphasized Title Medium text
 * @cssprop --md-sys-typescale-emphasized-title-medium-font-weight - Font weight for emphasized Title Medium text
 * @cssprop --md-sys-typescale-emphasized-title-medium-tracking - Letter spacing for emphasized Title Medium text
 * @cssprop --md-sys-typescale-emphasized-title-medium-line-height - Line height for emphasized Title Medium text
 * @cssprop --md-sys-typescale-emphasized-title-small-font-size - Font size for emphasized Title Small text
 * @cssprop --md-sys-typescale-emphasized-title-small-font-weight - Font weight for emphasized Title Small text
 * @cssprop --md-sys-typescale-emphasized-title-small-tracking - Letter spacing for emphasized Title Small text
 * @cssprop --md-sys-typescale-emphasized-title-small-line-height - Line height for emphasized Title Small text
 * @cssprop --md-sys-typescale-emphasized-label-large-font-size - Font size for emphasized Label Large text
 * @cssprop --md-sys-typescale-emphasized-label-large-font-weight - Font weight for emphasized Label Large text
 * @cssprop --md-sys-typescale-emphasized-label-large-tracking - Letter spacing for emphasized Label Large text
 * @cssprop --md-sys-typescale-emphasized-label-large-line-height - Line height for emphasized Label Large text
 * @cssprop --md-sys-typescale-emphasized-label-medium-font-size - Font size for emphasized Label Medium text
 * @cssprop --md-sys-typescale-emphasized-label-medium-font-weight - Font weight for emphasized Label Medium text
 * @cssprop --md-sys-typescale-emphasized-label-medium-tracking - Letter spacing for emphasized Label Medium text
 * @cssprop --md-sys-typescale-emphasized-label-medium-line-height - Line height for emphasized Label Medium text
 * @cssprop --md-sys-typescale-emphasized-label-small-font-size - Font size for emphasized Label Small text
 * @cssprop --md-sys-typescale-emphasized-label-small-font-weight - Font weight for emphasized Label Small text
 * @cssprop --md-sys-typescale-emphasized-label-small-tracking - Letter spacing for emphasized Label Small text
 * @cssprop --md-sys-typescale-emphasized-label-small-line-height - Line height for emphasized Label Small text
 */
@customElement("m3e-heading")
export class M3eHeadingElement extends LitElement {
  /** The styles of the element. */
  static override styles: CSSResultGroup = css`
    :host {
      display: block;
    }
    :host([variant="display"][size="large"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.display.large.fontSize};
      font-weight: ${DesignToken.typescale.standard.display.large.fontWeight};
      line-height: ${DesignToken.typescale.standard.display.large.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.display.large.tracking};
    }
    :host([variant="display"][size="medium"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.display.medium.fontSize};
      font-weight: ${DesignToken.typescale.standard.display.medium.fontWeight};
      line-height: ${DesignToken.typescale.standard.display.medium.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.display.medium.tracking};
    }
    :host([variant="display"][size="small"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.display.small.fontSize};
      font-weight: ${DesignToken.typescale.standard.display.small.fontWeight};
      line-height: ${DesignToken.typescale.standard.display.small.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.display.small.tracking};
    }
    :host([variant="headline"][size="large"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.headline.large.fontSize};
      font-weight: ${DesignToken.typescale.standard.headline.large.fontWeight};
      line-height: ${DesignToken.typescale.standard.headline.large.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.headline.large.tracking};
    }
    :host([variant="headline"][size="medium"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.headline.medium.fontSize};
      font-weight: ${DesignToken.typescale.standard.headline.medium.fontWeight};
      line-height: ${DesignToken.typescale.standard.headline.medium.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.headline.medium.tracking};
    }
    :host([variant="headline"][size="small"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.headline.small.fontSize};
      font-weight: ${DesignToken.typescale.standard.headline.small.fontWeight};
      line-height: ${DesignToken.typescale.standard.headline.small.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.headline.small.tracking};
    }
    :host([variant="title"][size="large"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.title.large.fontSize};
      font-weight: ${DesignToken.typescale.standard.title.large.fontWeight};
      line-height: ${DesignToken.typescale.standard.title.large.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.title.large.tracking};
    }
    :host([variant="title"][size="medium"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.title.medium.fontSize};
      font-weight: ${DesignToken.typescale.standard.title.medium.fontWeight};
      line-height: ${DesignToken.typescale.standard.title.medium.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.title.medium.tracking};
    }
    :host([variant="title"][size="small"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.title.small.fontSize};
      font-weight: ${DesignToken.typescale.standard.title.small.fontWeight};
      line-height: ${DesignToken.typescale.standard.title.small.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.title.small.tracking};
    }
    :host([variant="label"][size="large"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.label.large.fontSize};
      font-weight: ${DesignToken.typescale.standard.label.large.fontWeight};
      line-height: ${DesignToken.typescale.standard.label.large.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.label.large.tracking};
    }
    :host([variant="label"][size="medium"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.label.medium.fontSize};
      font-weight: ${DesignToken.typescale.standard.label.medium.fontWeight};
      line-height: ${DesignToken.typescale.standard.label.medium.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.label.medium.tracking};
    }
    :host([variant="label"][size="small"]:not([emphasized])) {
      font-size: ${DesignToken.typescale.standard.label.small.fontSize};
      font-weight: ${DesignToken.typescale.standard.label.small.fontWeight};
      line-height: ${DesignToken.typescale.standard.label.small.lineHeight};
      letter-spacing: ${DesignToken.typescale.standard.label.small.tracking};
    }
    :host([variant="display"][size="large"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.display.large.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.display.large.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.display.large.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.display.large.tracking};
    }
    :host([variant="display"][size="medium"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.display.medium.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.display.medium.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.display.medium.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.display.medium.tracking};
    }
    :host([variant="display"][size="small"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.display.small.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.display.small.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.display.small.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.display.small.tracking};
    }
    :host([variant="headline"][size="large"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.headline.large.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.headline.large.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.headline.large.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.headline.large.tracking};
    }
    :host([variant="headline"][size="medium"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.headline.medium.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.headline.medium.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.headline.medium.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.headline.medium.tracking};
    }
    :host([variant="headline"][size="small"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.headline.small.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.headline.small.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.headline.small.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.headline.small.tracking};
    }
    :host([variant="title"][size="large"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.title.large.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.title.large.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.title.large.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.title.large.tracking};
    }
    :host([variant="title"][size="medium"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.title.medium.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.title.medium.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.title.medium.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.title.medium.tracking};
    }
    :host([variant="title"][size="small"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.title.small.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.title.small.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.title.small.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.title.small.tracking};
    }
    :host([variant="label"][size="large"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.label.large.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.label.large.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.label.large.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.label.large.tracking};
    }
    :host([variant="label"][size="medium"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.label.medium.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.label.medium.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.label.medium.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.label.medium.tracking};
    }
    :host([variant="label"][size="small"][emphasized]) {
      font-size: ${DesignToken.typescale.emphasized.label.small.fontSize};
      font-weight: ${DesignToken.typescale.emphasized.label.small.fontWeight};
      line-height: ${DesignToken.typescale.emphasized.label.small.lineHeight};
      letter-spacing: ${DesignToken.typescale.emphasized.label.small.tracking};
    }
  `;

  /**
   * Whether the heading uses an emphasized typescale.
   * @default false
   */
  @property({ type: Boolean, reflect: true }) emphasized = false;

  /**
   * The appearance variant of the heading.
   * @default "display"
   */
  @property({ reflect: true }) variant: HeadingVariant = "display";

  /**
   * The size of the heading.
   * @default "medium"
   */
  @property({ reflect: true }) size: HeadingSize = "medium";

  /**
   * The accessibility level of the heading.
   * @default undefined
   */
  @property({ type: Number }) level?: HeadingLevel;

  /** @inheritdoc */
  protected override update(changedProperties: PropertyValues<this>): void {
    super.update(changedProperties);

    if (changedProperties.has("level")) {
      this.ariaLevel = this.level !== undefined ? `${this.level}` : null;
      this.role = this.ariaLevel ? "heading" : null;
    }
  }

  /** @inheritdoc */
  protected override render(): unknown {
    return html`<slot></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "m3e-heading": M3eHeadingElement;
  }
}
